import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  Copy03,
  Eraser,
  Plus,
  Trash04,
} from '@untitled-ui/icons-react';
import { useState } from 'react';

import { IconButton } from '../../../components/buttons/icon-button';
import {
  AppDropdownBody,
  DropdownMenu,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
} from '../../../components/dropdown-menu';
import { AnimatedDotsIcons } from '../../../components/icons/animatedDotsIcons';
import { LazyTooltip } from '../../../components/lazy-tooltip';
import { DropdownMenuOption, MenuOption } from '../../../components/menu-option';
import { tNamespace } from '../../../services/translation';

const t = tNamespace('shared.tiptap-custom-extensions.content-table-utilities');

export type ContentTableUtilitiesProps = {
  onDuplicate: () => unknown;
  onCopyTableHTML: () => unknown;
  onDelete: () => unknown;
  onAddColumnToTheEnd: () => unknown;
  onAddRowToTheEnd: () => unknown;
  onHighlightColumn: () => unknown;
  onHighlightRow: () => unknown;
  isHoveringInTableArea: boolean;
  shouldShowColumnAdder: boolean;
  shouldShowRowAdder: boolean;
  activeColumnIndex: number | null;
  setActiveColumnIndex: (value: number | null) => unknown;
  setActiveColumnIndexCandidate: (value: number | null) => unknown;
  activeRowIndex: number | null;
  setActiveRowIndex: (value: number | null) => unknown;
  setActiveRowIndexCandidate: (value: number | null) => unknown;
  topFloaterStyle: { left: number; width: number };
  leftFloaterStyle: { top: number; height: number };
  onAddColumn: (direction: 'left' | 'right') => unknown;
  onAddRow: (direction: 'up' | 'down') => unknown;
  onDeleteColumn: () => unknown;
  onDeleteRow: () => unknown;
  onMakeFirstRowHeader: () => unknown;
  onMakeFirstColumnHeader: () => unknown;
  onOmitHeader: () => unknown;
};

export function ContentTableUtilities(props: ContentTableUtilitiesProps) {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  return (
    <>
      <div className="absolute -right-7 -top-7 pb-2">
        <DropdownMenu
          open={isOptionsOpen}
          onOpenChange={setIsOptionsOpen}
          trigger={
            <div className="group/dots-icon flex size-6 cursor-pointer items-center justify-center rounded-md border border-branding-gray">
              <LazyTooltip text={t('tableOptions')}>
                <IconButton
                  isDiv={true}
                  size="xs"
                  variant="ghost-neutral"
                  icon={<AnimatedDotsIcons groupName="group/dots-icon" />}
                />
              </LazyTooltip>
            </div>
          }
          triggerClassName="group/dots-icon"
        >
          <AppDropdownBody>
            <DropdownSub>
              <DropdownSubTrigger>
                <MenuOption
                  mainText={t('header')}
                  rightElement={<ChevronRight width={16} height={16} />}
                />
              </DropdownSubTrigger>
              <DropdownSubContent sideOffset={15} alignOffset={-10}>
                <AppDropdownBody className="w-50">
                  <DropdownMenuOption
                    mainText={t('makeFirstColumnHeader')}
                    leftElement={<ArrowDown width={16} height={16} className="text-black" />}
                    onClick={function () {
                      props.onMakeFirstColumnHeader();
                      setIsOptionsOpen(false);
                    }}
                  />
                  <DropdownMenuOption
                    leftElement={<ArrowRight width={16} height={16} className="text-black" />}
                    mainText={t('makeFirstRowHeader')}
                    onClick={function () {
                      props.onMakeFirstRowHeader();
                      setIsOptionsOpen(false);
                    }}
                  />
                  <DropdownMenuOption
                    mainText={t('omitHeader')}
                    leftElement={<Eraser width={16} height={16} className="text-black" />}
                    onClick={function () {
                      props.onOmitHeader();
                      setIsOptionsOpen(false);
                    }}
                  />
                </AppDropdownBody>
              </DropdownSubContent>
            </DropdownSub>
            <DropdownMenuOption
              mainText={t('duplicate')}
              leftElement={<Copy03 width={16} height={16} className="text-black" />}
              onClick={function () {
                props.onDuplicate();
                setIsOptionsOpen(false);
              }}
            />
            <DropdownMenuOption
              mainText={t('copyToClipboard')}
              leftElement={<Copy03 width={16} height={16} className="text-black" />}
              onClick={function () {
                props.onCopyTableHTML();
                setIsOptionsOpen(false);
              }}
            />
            <DropdownMenuOption
              mainText={t('deleteTable')}
              leftElement={<Trash04 width={16} height={16} className="text-red-500" />}
              onClick={function () {
                props.onDelete();
                setIsOptionsOpen(false);
              }}
            />
          </AppDropdownBody>
        </DropdownMenu>
      </div>
      {props.isHoveringInTableArea ? (
        <>
          <div
            className={`absolute left-full top-0 h-full ${props.shouldShowColumnAdder ? 'block' : 'hidden'} px-2`}
          >
            <LazyTooltip text={t('addColumn')}>
              <button
                className="flex h-full w-6 cursor-pointer items-center justify-center rounded-md border border-branding-gray bg-white"
                onClick={props.onAddColumnToTheEnd}
              >
                <Plus width={16} height={16} className="text-branding-gray-700" />
              </button>
            </LazyTooltip>
          </div>
          <div
            className={`absolute left-0 top-full w-full py-2 ${props.shouldShowRowAdder ? 'block' : 'hidden'}`}
          >
            <LazyTooltip text={t('addRow')}>
              <button
                className="flex h-6 w-full cursor-pointer items-center justify-center rounded-md border border-branding-gray bg-white"
                onClick={props.onAddRowToTheEnd}
              >
                <Plus width={16} height={16} className="text-branding-gray-700" />
              </button>
            </LazyTooltip>
          </div>
          {props.activeColumnIndex == null ? (
            <div className="absolute bottom-full py-2" style={props.topFloaterStyle}>
              <LazyTooltip text={t('selectColumn')}>
                <button
                  className="flex h-5 w-full items-center justify-center rounded-md border border-branding-gray bg-white"
                  onClick={props.onHighlightColumn}
                >
                  <ArrowDown width={16} height={16} className="text-branding-gray-700" />
                </button>
              </LazyTooltip>
            </div>
          ) : (
            <div
              className="absolute bottom-full w-auto py-2"
              style={{ left: props.topFloaterStyle.left }}
              onMouseLeave={function () {
                props.setActiveColumnIndex(null);
                props.setActiveColumnIndexCandidate(null);
              }}
            >
              <div className="flex items-center justify-center gap-1 rounded-md border border-branding-gray bg-white px-3 py-1 shadow-2">
                <div className="flex gap-1">
                  <LazyTooltip text={t('addColumnLeft')}>
                    <IconButton
                      variant="ghost-neutral"
                      onClick={function () {
                        props.onAddColumn('left');
                      }}
                      icon={ArrowLeft}
                      aria-label={t('addColumnLeft')}
                    />
                  </LazyTooltip>
                  <LazyTooltip text={t('addColumnRight')}>
                    <IconButton
                      variant="ghost-neutral"
                      onClick={function () {
                        props.onAddColumn('right');
                      }}
                      icon={ArrowRight}
                      aria-label={t('addColumnRight')}
                    />
                  </LazyTooltip>
                </div>
                <LazyTooltip text={t('deleteColumn')}>
                  <IconButton
                    onClick={props.onDeleteColumn}
                    icon={Trash04}
                    variant="ghost-neutral"
                    aria-label={t('deleteColumn')}
                    className="text-red-500"
                  />
                </LazyTooltip>
              </div>
            </div>
          )}
          {props.activeRowIndex == null ? (
            <div className="absolute right-full px-2" style={props.leftFloaterStyle}>
              <LazyTooltip text={t('selectRow')}>
                <button
                  className="flex h-full w-6 items-center justify-center rounded-md border border-branding-gray bg-white"
                  onClick={props.onHighlightRow}
                >
                  <ArrowRight width={16} height={16} className="text-branding-gray-700" />
                </button>
              </LazyTooltip>
            </div>
          ) : (
            <div
              className="absolute right-full px-2"
              style={props.leftFloaterStyle}
              onMouseLeave={function () {
                props.setActiveRowIndex(null);
                props.setActiveRowIndexCandidate(null);
              }}
            >
              <div className="grid grid-flow-row gap-1 rounded-md border border-branding-gray bg-white px-1 py-3 shadow-2">
                <LazyTooltip text={t('addRowAbove')}>
                  <IconButton
                    onClick={function () {
                      props.onAddRow('up');
                    }}
                    aria-label={t('addRowAbove')}
                    variant="ghost-neutral"
                    icon={ArrowUp}
                  />
                </LazyTooltip>
                <LazyTooltip text={t('addRowBelow')}>
                  <IconButton
                    onClick={function () {
                      props.onAddRow('down');
                    }}
                    aria-label={t('addRowBelow')}
                    variant="ghost-neutral"
                    icon={ArrowDown}
                  />
                </LazyTooltip>
                <LazyTooltip text={t('deleteRow')}>
                  <IconButton
                    onClick={props.onDeleteRow}
                    aria-label={t('deleteRow')}
                    variant="ghost-neutral"
                    icon={Trash04}
                    className="text-red-500"
                  />
                </LazyTooltip>
              </div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
